// team/components/support/TeamRequestAFeatureModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, IconButton, TextField, Button, Stack, CircularProgress } from '@mui/material';
import { CloseIcon } from 'icons/materialIcons';
import { useAuth } from 'contexts/AuthContext'; // Adjust path as needed
import axios from 'axios';
import { API_BASE_URL } from 'utils/apiUtils'; // Adjust path as needed

const TeamRequestAFeatureModal = ({ open, onClose, onSubmitSuccess, onSubmitError }) => {
  const [featureRequest, setFeatureRequest] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { authState } = useAuth();

  useEffect(() => {
    if (open) {
      setFeatureRequest('');
    }
  }, [open]);

  const buttonStyles = {
    padding: '8px 45px',
    textTransform: 'none',
    borderRadius: '26px',
  };

  const isSendDisabled = featureRequest.trim().length === 0 || isSubmitting;

  const handleSendFeedback = async () => {
    if (!featureRequest.trim()) return;

    setIsSubmitting(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/email/feature-request`,
        { featureRequest },
        {
          headers: {
            Authorization: `Bearer ${authState.token}`
          }
        }
      );

      onSubmitSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error sending feature request:', error);
      onSubmitError?.(error.response?.data?.message || 'Failed to send feature request');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="request-feature-modal"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(8px)',
        bgcolor: 'rgba(255, 255, 255, 0.8)',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: { xs: '90%', sm: '75%', md: '70%', lg: '60%' },
          maxWidth: 700,
          minHeight: 400,
          bgcolor: '#ffffff',
          borderRadius: 5,
          boxShadow: 24,
          p: "50px"
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', top: 16, right: 16 }}
          disabled={isSubmitting}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          id="request-feature-modal-title"
          variant="h5"
          component="h2"
          gutterBottom
        >
          Request a Feature
        </Typography>

        <Typography
          variant="body1"
          sx={{ mb: 2, fontSize: '0.9rem', color: '#828282' }}
        >
          Do you have an idea for a feature that would make Touch even better for you? Let us know!
        </Typography>

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Message..."
          multiline
          rows={4}
          value={featureRequest}
          onChange={(e) => setFeatureRequest(e.target.value)}
          disabled={isSubmitting}
          sx={{
            mb: 2,
            bgcolor: '#f7f7f7',
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
          }}
        />

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isSubmitting}
            sx={{
              ...buttonStyles,
              bgcolor: 'transparent',
              color: '#212121',
              fontWeight: '500',
              border: '1px solid #c4c4c4',
            }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={handleSendFeedback}
            disabled={isSendDisabled}
            sx={{
              ...buttonStyles,
              bgcolor: isSendDisabled ? '#e0e0e0' : '#000000',
              color: isSendDisabled ? '#a6a6a6' : '#ffffff',
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Send Feedback'
            )}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

export default TeamRequestAFeatureModal;